import { Link, useParams } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  ArrowLeft,
  Building2,
  Landmark,
  Users,
  MapPin,
  Briefcase,
  Trophy,
  XOctagon,
  TrendingUp,
  ChevronRight,
  Phone,
  Mail,
  Crown,
  Activity,
  Banknote,
  CheckCircle2,
  XCircle,
  UserCircle,
  AlertTriangle,
} from "lucide-react";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const TYPE_CFG: Record<
  string,
  {
    label: string;
    short: string;
    icon: any;
    color: string;
    bg: string;
    ring: string;
  }
> = {
  public_admin: {
    label: "Órgão público",
    short: "B2G",
    icon: Landmark,
    color: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    ring: "ring-amber-200 dark:ring-amber-800",
  },
  empresa: {
    label: "Empresa",
    short: "B2B",
    icon: Building2,
    color: "text-sky-700 dark:text-sky-300",
    bg: "bg-sky-50 dark:bg-sky-950/40",
    ring: "ring-sky-200 dark:ring-sky-800",
  },
  cliente_final: {
    label: "Pessoa",
    short: "B2C",
    icon: Users,
    color: "text-emerald-700 dark:text-emerald-300",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    ring: "ring-emerald-200 dark:ring-emerald-800",
  },
};

const ROLE_CFG: Record<string, { label: string; icon: any; color: string }> = {
  decisor: { label: "Decisor", icon: Crown, color: "text-amber-600" },
  influenciador: {
    label: "Influenciador",
    icon: Users,
    color: "text-sky-600",
  },
  tecnico: { label: "Técnico", icon: Activity, color: "text-purple-600" },
  financeiro: {
    label: "Financeiro",
    icon: Banknote,
    color: "text-emerald-600",
  },
  usuario_final: {
    label: "Usuário final",
    icon: UserCircle,
    color: "text-slate-600",
  },
};

function CrmOrganizationDetailBody({ name }: { name: string }) {
  const query = trpc.crm.organizations.get.useQuery({ name });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // Mesmo branch da listagem — backend pode lançar PRECONDITION_FAILED quando
  // o tenant excede o cap, e a mensagem do tRPC traz instrução acionável.
  // Sem este branch o usuário cairia em "Organização não encontrada" (caminho
  // de !query.data), o que esconde a causa real.
  if (query.isError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Não foi possível carregar a organização</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{query.error?.message ?? "Erro desconhecido."}</p>
          <Link
            href="/crm/organizations"
            className="text-sm text-primary hover:underline inline-block"
          >
            Voltar à lista
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  if (!query.data) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="font-semibold">Organização não encontrada.</p>
          <Link
            href="/crm/organizations"
            className="text-sm text-primary hover:underline mt-2 inline-block"
          >
            Voltar à lista
          </Link>
        </CardContent>
      </Card>
    );
  }

  const org = query.data;
  const type = org.contactType ?? "empresa";
  const cfg = TYPE_CFG[type] ?? TYPE_CFG.empresa;
  const TypeIcon = cfg.icon;
  const location = [org.city, org.state].filter(Boolean).join(" · ");

  const openDeals = org.deals.filter((d: any) => d.status === "open");
  const wonDeals = org.deals.filter((d: any) => d.status === "won");
  const lostDeals = org.deals.filter((d: any) => d.status === "lost");
  const openValue = openDeals.reduce(
    (acc: number, d: any) => acc + Number(d.value ?? 0),
    0,
  );
  const wonValue = wonDeals.reduce(
    (acc: number, d: any) => acc + Number(d.value ?? 0),
    0,
  );

  return (
    <div className="space-y-5">
      {/* ── Breadcrumb ──────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground overflow-x-auto -mx-1 px-1 whitespace-nowrap">
        <Link
          href="/crm/organizations"
          className="hover:text-foreground flex items-center gap-1 shrink-0"
        >
          <ArrowLeft className="h-3 w-3" />
          Organizações
        </Link>
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="truncate max-w-[300px] font-medium text-foreground">
          {org.name}
        </span>
      </div>

      {/* ── Hero ────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div
              className={`h-14 w-14 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg} ${cfg.color} ring-1 ${cfg.ring}`}
            >
              <TypeIcon className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge
                  variant="outline"
                  className={`${cfg.bg} ${cfg.color} border-transparent text-[10px] h-5`}
                >
                  {cfg.short} · {cfg.label}
                </Badge>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold leading-tight break-words">
                {org.name}
              </h1>
              {location && (
                <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {location}
                </div>
              )}
            </div>
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <HeroMetric
              label="Pessoas"
              value={String(org.contacts.length)}
              icon={Users}
            />
            <HeroMetric
              label="Oportunidades"
              value={String(openDeals.length)}
              sub={`${wonDeals.length} ganhas · ${lostDeals.length} perdidas`}
              icon={Briefcase}
              tint="primary"
            />
            <HeroMetric
              label="Pipeline aberto"
              value={formatBRL(openValue)}
              icon={TrendingUp}
              tint={openValue > 0 ? "primary" : "default"}
            />
            <HeroMetric
              label="Total ganho"
              value={formatBRL(wonValue)}
              icon={Trophy}
              tint={wonValue > 0 ? "emerald" : "default"}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Pessoas da organização ──────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Pessoas na {cfg.label.toLowerCase()}
            <Badge variant="secondary">{org.contacts.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {org.contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma pessoa.</p>
          ) : (
            org.contacts.map((c: any) => {
              const fullName = [c.name, c.lastName].filter(Boolean).join(" ");
              return (
                <Link
                  key={c.id}
                  href={`/contacts/${c.id}`}
                  className="flex items-center gap-3 rounded-lg border px-3 py-2 hover:bg-muted/40 transition"
                >
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                      {initials(fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {fullName || "Sem nome"}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      {c.sector && (
                        <span className="truncate max-w-[200px]">
                          {c.sector}
                        </span>
                      )}
                      {c.phoneNumber && (
                        <span className="flex items-center gap-1 font-mono">
                          <Phone className="h-3 w-3" />
                          {c.phoneNumber}
                        </span>
                      )}
                      {c.email && (
                        <span className="flex items-center gap-1 truncate max-w-[200px]">
                          <Mail className="h-3 w-3" />
                          {c.email}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.dealCount > 0 && (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5"
                        title="Oportunidades como contato principal"
                      >
                        <Briefcase className="h-2.5 w-2.5 mr-1" />
                        {c.dealCount}
                      </Badge>
                    )}
                    {c.participantCount > 0 && (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5"
                        title="Participações em outras oportunidades"
                      >
                        <Users className="h-2.5 w-2.5 mr-1" />
                        {c.participantCount}
                      </Badge>
                    )}
                  </div>
                </Link>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* ── Oportunidades da organização ────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Oportunidades
            <Badge variant="secondary">{org.deals.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {org.deals.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="h-10 w-10 mx-auto mb-2 text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground">
                Nenhuma oportunidade registrada ainda para esta organização.
              </p>
            </div>
          ) : (
            org.deals.map((d: any) => (
              <DealRow key={d.id} deal={d} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DealRow({ deal }: { deal: any }) {
  const isWon = deal.status === "won";
  const isLost = deal.status === "lost";
  const participants: any[] = deal.participants ?? [];
  const decisores = participants.filter((p) => p.role === "decisor");
  const outros = participants.filter((p) => p.role !== "decisor");

  return (
    <Link
      href={`/crm/deals/${deal.id}`}
      className={`block rounded-lg border p-3 hover:bg-muted/40 transition relative overflow-hidden ${
        isWon
          ? "border-emerald-200 dark:border-emerald-900"
          : isLost
          ? "border-red-200 dark:border-red-900 opacity-80"
          : ""
      }`}
    >
      {isWon && <div className="absolute inset-y-0 left-0 w-1 bg-emerald-500" />}
      {isLost && <div className="absolute inset-y-0 left-0 w-1 bg-red-500" />}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isWon && (
              <Badge className="bg-emerald-600 h-5 text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Fechada
              </Badge>
            )}
            {isLost && (
              <Badge className="bg-red-600 h-5 text-[10px]">
                <XCircle className="h-3 w-3 mr-1" />
                Perdida
              </Badge>
            )}
            {!isWon && !isLost && (
              <Badge variant="outline" className="h-5 text-[10px]">
                Em aberto
              </Badge>
            )}
            <span className="text-sm font-semibold truncate">{deal.title}</span>
          </div>

          {/* Contato principal */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
            <UserCircle className="h-3 w-3" />
            <span className="truncate">{deal.contactName || "Sem contato"}</span>
            <span className="text-muted-foreground/70">(contato principal)</span>
          </div>

          {/* Participantes (decisores em destaque) */}
          {participants.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {decisores.map((p) => (
                <ParticipantChip key={`d-${p.contactId}`} p={p} />
              ))}
              {outros.slice(0, 3).map((p) => (
                <ParticipantChip key={`o-${p.contactId}`} p={p} />
              ))}
              {outros.length > 3 && (
                <span className="text-[10px] text-muted-foreground self-center">
                  +{outros.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="text-right shrink-0">
          <div className="text-base font-bold tabular-nums">
            {formatBRL(deal.value)}
          </div>
          {deal.closedAt && (
            <div className="text-[11px] text-muted-foreground">
              {isWon ? "Fechado em " : isLost ? "Perdido em " : ""}
              {new Date(deal.closedAt).toLocaleDateString("pt-BR")}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function ParticipantChip({ p }: { p: any }) {
  const cfg = ROLE_CFG[p.role] ?? ROLE_CFG.influenciador;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.color}`}
      title={`${cfg.label}${p.companyName ? ` · ${p.companyName}` : ""}`}
    >
      <Icon className="h-2.5 w-2.5" />
      <span className="truncate max-w-[140px]">{p.contactName}</span>
    </span>
  );
}

function HeroMetric({
  label,
  value,
  sub,
  icon: Icon,
  tint = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tint?: "default" | "primary" | "emerald";
}) {
  const tintMap = {
    default: "bg-muted text-muted-foreground",
    primary: "bg-primary/10 text-primary",
    emerald:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  };
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
        <div
          className={`h-5 w-5 rounded flex items-center justify-center ${tintMap[tint]}`}
        >
          <Icon className="h-3 w-3" />
        </div>
        {label}
      </div>
      <div className="text-lg sm:text-xl font-bold tabular-nums">{value}</div>
      {sub && (
        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

export default function CrmOrganizationDetail() {
  const params = useParams<{ slug: string }>();
  const name = params.slug ? decodeURIComponent(params.slug) : "";
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:deals_view">
        {name ? (
          <CrmOrganizationDetailBody name={name} />
        ) : (
          <div className="py-12 text-center">Nome inválido.</div>
        )}
      </PermissionGuard>
    </DashboardLayout>
  );
}
